import { nameCase } from '@foundernest/namecase'

const ORDINAL_MAP: Record<string, string> = {
  JR: 'Jr.', 'JR.': 'Jr.',
  SR: 'Sr.', 'SR.': 'Sr.',
}

const FORCE_UPPER = [
  'MD', 'DO', 'PHD', 'DDS', 'DMD', 'NP', 'PA', 'RN', 'APRN', 'NP-C', 'CNA',
  'RPSGT', 'FAASM', 'DABSM', 'CNS', 'MBA', 'JD', 'LPN', 'LVN',
  'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX',
]

/** Apply intelligent name-casing to a single name segment. */
export const nc = (name?: string | null): string =>
  name?.trim() ? nameCase(name.trim()) : ''

/** Normalize a suffix or credential token. */
export const normalizeSuffix = (value?: string | null): string => {
  if (!value?.trim()) return ''
  const raw = value.trim()
  const upper = raw.toUpperCase().replace(/\.$/, '')
  if (ORDINAL_MAP[upper]) return ORDINAL_MAP[upper]
  if (FORCE_UPPER.includes(upper)) return upper === 'PHD' ? 'PhD' : upper
  return raw
}

/** "John Smith" */
export const fullName = (
  firstName?: string | null,
  lastName?: string | null,
): string => [nc(firstName), nc(lastName)].filter(Boolean).join(' ')

/** "Smith, John" */
export const formalName = (
  firstName?: string | null,
  lastName?: string | null,
): string => {
  const last = nc(lastName)
  const first = nc(firstName)
  if (!last && !first) return ''
  if (!last) return first
  if (!first) return last
  return `${last}, ${first}`
}

/** "Smith Jr., John Michael" */
export const patientFormalName = (
  firstName?: string | null,
  lastName?: string | null,
  middleName?: string | null,
  suffix?: string | null,
): string => {
  const last = nc(lastName)
  const suf = normalizeSuffix(suffix)
  const lastSuf = suf && suf !== 'Suffix' ? `${last} ${suf}` : last
  const first = nc(firstName)
  const mid = nc(middleName)
  const firstFull = mid ? `${first} ${mid}` : first
  if (!lastSuf && !firstFull) return ''
  if (!lastSuf) return firstFull
  if (!firstFull) return lastSuf
  return `${lastSuf}, ${firstFull}`.trim()
}

/** "John Smith, MD" */
export const physicianFullName = (
  firstName?: string | null,
  lastName?: string | null,
  credential?: string | null,
): string => {
  const name = fullName(firstName, lastName)
  const cred = normalizeSuffix(credential)
  if (!name) return ''
  return cred ? `${name}, ${cred}` : name
}

/** "Smith, John MD" */
export const physicianFormalName = (
  firstName?: string | null,
  lastName?: string | null,
  credential?: string | null,
): string => {
  const formal = formalName(firstName, lastName)
  const cred = normalizeSuffix(credential)
  if (!formal) return ''
  return cred ? `${formal} ${cred}` : formal
}

/**
 * Whether a name label holds every word typed, in any order: "Victoria Smith"
 * and "smith vic" both match "Smith, Victoria".
 */
export const matchesEveryWord = (label: string, input: string): boolean => {
  const name = label.toLowerCase()
  return input
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean)
    .every((word) => name.includes(word))
}
